import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Mail, Lock, LogIn, Shield, User } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Ensure no auto-scaling affects this page
  useEffect(() => {
    // Add class to html and body for fallback support
    document.documentElement.classList.add('admin-login-page');
    document.body.classList.add('admin-login-page');
    
    // Disable any existing auto-scale
    document.documentElement.style.zoom = '1';
    document.documentElement.style.transform = 'none';
    document.body.style.zoom = '1';
    document.body.style.transform = 'none';
    
    return () => {
      // Cleanup when component unmounts
      document.documentElement.classList.remove('admin-login-page');
      document.body.classList.remove('admin-login-page');
      // Don't restore zoom/transform here as user might navigate to other pages
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/admin');
    } else if (result.notAdmin) {
      setError('Access denied. You are not authorized to access the admin panel.');
    } else {
      setError(result.error || 'Invalid email or password');
    }
    
    setLoading(false);
  };

  return (
    <div className="static-admin-login">
      {/* Background */}
      <div className="login-background">
        <div className="login-pattern"></div>
        <div className="login-shape-1"></div>
        <div className="login-shape-2"></div>
        <div className="login-shape-3"></div>
      </div>

      {/* Main Login Container */}
      <div className="login-container">
        <div className="login-card">
          {/* Header */}
          <div className="login-header">
            <div className="login-icon">
              <Shield className="icon" />
            </div>
            <h1 className="login-title">Admin Portal</h1>
            <p className="login-subtitle">Secure access to dashboard</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-error">
                {error}
              </div>
            )}

            {/* Email Field */}
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                  placeholder="admin@example.com"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="login-button"
            >
              {loading ? (
                <>
                  <div className="loading-spinner" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn className="button-icon" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <User className="footer-icon" />
            <span>Authorized personnel only</span>
          </div>
        </div>
      </div>
    </div>
  );
}
